import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import moment from "moment";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { getPaymentCustomPlan } from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import { formatDisplayDate } from "utils/DateFormat";
import {
  cardToShortname,
  formatCurrency,
  getCountDownAgreement,
  isExpired,
  rowHelperText,
} from "utils/HelperFunctions";
interface PlanPreviewProps {
  id: string;
  setId: any;
  show: boolean;
  setShow: any;
  onClose: () => void;
  tempData: any;
  orderData: any;
  selectedMethod: string;
  paymentMethodToken: string;
  isStax?: boolean;
}

const PayPreview = ({
  onClose,
  tempData,
  orderData,
  selectedMethod,
  show,
  paymentMethodToken,
  isStax = false,
}: PlanPreviewProps) => {
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const [isFeeLoading, setIsFeeLoading] = useState(true);
  const [data, setData] = useState<any>({});
  const { conFee } = useAppSelector((state: RootState) => state.payment);

  useEffect(() => {
    if (show && selectedMethod) {
      handleGetConvFee();
    }
  }, [show, selectedMethod]);

  const handleGetConvFee = async () => {
    try {
      let payload = {
        date_array: tempData
          ? tempData?.map((itm: any) => {
              return {
                amount: itm.amount,
                balance: userData?.data?.accountData?.balance,
                fee: selectedMethod === "PP" ? 0 : itm.cf_amount || conFee,
                payment_date: itm.billing_date || itm.date || itm.payment_date,
              };
            })
          : [],
        lead_id: orderData?.lead_id,
        pm_id: paymentMethodToken,
        cluster_id: orderData?.clusterid,
        balance: userData?.data?.accountData?.balance,
        schedule_amount:
          tempData?.[0]?.schedule_type?.toLowerCase() || "custom",
        custom_conv: orderData?.custom_conv,
        payment_method: selectedMethod,
        no_of_transaction: tempData?.length || 5,
        grand_total: "0.00",
        fee_subtotal: "0.00",
        amount_total: "0.00",
        start_date: tempData?.[0]?.billing_date,
        schedule_fld: orderData?.payment_schedule || "daily",
        amount: "0.00",
        transaction_type: "amount",
        process_payment: true,
        isAmount: true,
        isDelete: false,
      };

      const res: any = await dispatch(getPaymentCustomPlan(payload)).unwrap();
      if (res?.body?.success) {
        setData(res?.body?.data);
      } else {
        setData(payload);
      }
    } catch (error) {
      console.log("handleGetConvFee error =====", error);
    } finally {
      setIsFeeLoading(false);
    }
  };

  let isFee = isFeeLoading || data.fee_subtotal > 0;

  return (
    <>
      <div className="bg-bgSecondary h-[var(--shell-vh)] overflow-y-auto pt-[70px]">
        <div className="px-5 lg:px-10 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-30 border-b-[1px] border-strokePrimary">
          <div className="flex items-center gap-2">
            <EyeIcon
              strokeColor="var(--color-txt-primary)"
              width="24"
              height="24"
            />
            <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
              Preview
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div
              onClick={() => {
                setIsFeeLoading(true);
                onClose();
              }}
              className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
            >
              <CloseIcon />
            </div>
          </div>
        </div>
        <div
          className={`lg:px-10 py-4 px-5 ${
            isExpired(
              getCountDownAgreement(
                orderData?.pp_oe_time,
                orderData.next_billing_date,
                orderData.creation_date,
                userData?.data?.accountData?.timezone
              ).date
            )
              ? "bg-lightRed2"
              : "bg-white border-t-[5px] border-strokePrimary"
          }`}
        >
          <p className="md:text-base text-sm font-medium text-txtPrimary">
            Payment agreement expires on{" "}
            <span className="font-bold text-btnPrimaryActive">
              {orderData?.expiry_date
                ? moment(orderData.expiry_date).format("MMM D, YYYY")
                : orderData?.expiryDate
                  ? moment(orderData.expiryDate).format("MMM D, YYYY")
                  : getCountDownAgreement(
                      orderData?.pp_oe_time,
                      orderData.next_billing_date,
                      orderData.creation_date,
                      userData?.data?.accountData?.timezone
                    ).adjustedDate}
            </span>{" "}
          </p>
        </div>
        <div className="lg:px-10 px-5">
          <p className="lg:text-xl text-lg font-normal py-4 text-txtPrimary">
            Payment Schedule
          </p>
          <div className="w-full py-2 items-start justify-start gap-1 flex flex-col">
            <p className="w-full text-xs font-normal text-txtSecondary">
              AMOUNT
            </p>
            <p className="w-full text-2xl text-txtPrimary">
              {isFeeLoading ? (
                <Skeleton className="w-full" />
              ) : (
                formatCurrency(data.grand_total) || "$0.00"
              )}
            </p>
          </div>
          <div className="py-2">
            <div className="lg:px-6 px-4 py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="flex flex-col items-start gap-5 w-full">
                <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                  SCHEDULE
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    !isFee ? "min-w-[338px]" : "min-w-[384px]"
                  }`}
                />

                <div className="flex items-center gap-1 w-full justify-between">
                  <div
                    className={`min-w-[35px] w-[7%] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    #
                  </div>
                  <div
                    className={`min-w-[92px] w-[32%] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Description
                  </div>
                  <div
                    className={`min-w-[100px] ${
                      isFee ? "w-[36%]" : "w-[20%]"
                    } text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>
                  {isFeeLoading ? (
                    <div
                      className={`min-w-[80px] w-[5%] text-sm text-txtParagraph leading-normal text-left`}
                    >
                      <Skeleton width={100} />
                    </div>
                  ) : (
                    <>
                      {isFee && (
                        <>
                          <div
                            className={`min-w-[80px] w-[5%] text-sm text-txtParagraph leading-normal text-left`}
                          >
                            {isStax ? "Surcharge" : "Fee"}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <div
                    className={`min-w-[96px] w-[20%] text-sm text-txtParagraph leading-normal text-start md:text-end`}
                  >
                    Amount
                  </div>
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    !isFee ? "min-w-[338px]" : "min-w-[384px]"
                  }`}
                />
                <div className="w-full flex flex-col gap-5">
                  {isFeeLoading
                    ? [1, 2, 3, 4, 5].map((_, index) => (
                        <div key={index}>
                          <div className="flex justify-between items-center gap-1">
                            <p className="min-w-[35px] w-[7%] text-sm text-txtParagraph font-normal">
                              {index + 1}
                              <span className="text-xs lowercase">
                                {rowHelperText(index + 1)}
                              </span>
                            </p>
                            <p className="min-w-[92px] w-[32%] text-sm text-txtPrimary font-normal">
                              Installment {index + 1}
                            </p>
                            <p
                              className={`min-w-[100px] ${
                                isFee ? "w-[36%]" : "w-[20%]"
                              } text-sm text-txtPrimary font-normal`}
                            >
                              <Skeleton className="w-full" />
                            </p>

                            <p
                              className={`min-w-[80px] w-[5%] text-sm text-txtPrimary leading-normal text-left`}
                            >
                              <Skeleton className="w-full" />
                            </p>

                            <p className="min-w-[96px] w-[20%] text-sm text-txtPrimary font-normal text-end">
                              <Skeleton className="w-full" />
                            </p>
                          </div>
                        </div>
                      ))
                    : data?.date_array?.map((details: any, index: number) => {
                        return (
                          <div key={index}>
                            <div className="flex justify-between items-center gap-1">
                              <p className="min-w-[35px] w-[7%] text-sm text-txtParagraph font-normal">
                                {index + 1}
                                <span className="text-xs lowercase">
                                  {rowHelperText(index + 1)}
                                </span>
                              </p>
                              <p className="min-w-[92px] w-[32%] text-sm text-txtPrimary font-normal">
                                Installment {index + 1}
                              </p>
                              <p
                                className={`min-w-[100px] ${
                                  isFee ? "w-[36%]" : "w-[20%]"
                                } text-sm text-txtPrimary font-normal`}
                              >
                                {formatDisplayDate(
                                  details.payment_date,
                                  "MM/DD/YYYY"
                                )}
                              </p>
                              {data.fee_subtotal > 0 && (
                                <>
                                  <p
                                    className={`min-w-[80px] w-[5%] text-sm text-txtPrimary leading-normal text-left`}
                                  >
                                    {formatCurrency(details.fee) || "$0.00"}
                                  </p>
                                </>
                              )}
                              <p className="min-w-[96px] w-[20%] text-sm text-txtPrimary font-normal text-start md:text-end">
                                {formatCurrency(details.amount) || "$0.00"}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    !isFee ? "min-w-[338px]" : "min-w-[384px]"
                  }`}
                />
                <div
                  className={`flex w-full justify-between items-center ${
                    !isFee ? "min-w-[328px]" : "min-w-[415px]"
                  }`}
                >
                  <div>
                    <p className="text-sm text-txtPrimary font-medium uppercase ">
                      SubTotal
                    </p>
                  </div>
                  <div className="flex items-center justify-start">
                    {data.fee_subtotal > 0 && (
                      <p className="text-sm text-txtPrimary font-bold min-w-[80px]">
                        {isFeeLoading ? (
                          <Skeleton className="w-full" />
                        ) : (
                          formatCurrency(data.fee_subtotal || 0) || "$0.00"
                        )}
                      </p>
                    )}
                    <p className="text-sm text-txtPrimary font-bold min-w-[96px] md:text-end">
                      {isFeeLoading ? (
                        <Skeleton className="w-full" />
                      ) : (
                        formatCurrency(orderData?.totalAmount) || "$0.00"
                      )}
                    </p>
                  </div>
                </div>
                <div
                  className={`h-[1px] w-full bg-strokePrimary ${
                    !isFee ? "min-w-[338px]" : "min-w-[384px]"
                  }`}
                />
                <div
                  className={`flex w-full justify-between items-center ${
                    !isFee ? "min-w-[328px]" : "min-w-[384px]"
                  }`}
                >
                  <p className="text-sm text-txtPrimary font-bold uppercase">
                    Total
                  </p>
                  <p
                    className={`text-lg text-txtPrimary font-bold pr-4 md:pr-0 md:min-w-[96px] md:text-end`}
                  >
                    {isFeeLoading ? (
                      <Skeleton className="w-full" />
                    ) : (
                      formatCurrency(data.grand_total) || "$0.00"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PayPreview;
